import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaArrowUp, FaArrowDown, FaMinus, FaStar, FaClipboardCheck } from "react-icons/fa";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { API_BASE_URL } from "../../config/apiConfig";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const TrendIcon = ({ trend }) => {
    if (trend > 2) return <FaArrowUp className="text-green-500" size={12} />;
    if (trend < -2) return <FaArrowDown className="text-red-500" size={12} />;
    return <FaMinus className="text-gray-400" size={12} />;
};

const trendLabel = (trend) => {
    if (trend > 2) return "Improving";
    if (trend < -2) return "Slipping";
    return "Steady";
};

const gradeColor = (grade) => {
    if (!grade) return "bg-gray-100 text-gray-500";
    if (["A1", "B2", "B3"].includes(grade)) return "bg-green-100 text-green-700";
    if (["C4", "C5", "C6"].includes(grade)) return "bg-amber-100 text-amber-700";
    return "bg-red-100 text-red-600";
};

// Confidence reflects how much practice data the projection is actually
// backed by, not the score itself — a high score from 4 lucky attempts
// should read very differently from the same score built on 100 attempts.
const confidenceFor = (totalAttempts) => {
    if (totalAttempts >= 40) return { label: "Solid data", tone: "text-green-600 bg-green-50" };
    if (totalAttempts >= 12) return { label: "Building a picture", tone: "text-amber-600 bg-amber-50" };
    return { label: "Just getting started", tone: "text-gray-500 bg-gray-100" };
};

const ScoreGauge = ({ score, max = 400 }) => {
    const pct = Math.max(0, Math.min(1, score / max));
    const data = [
        { name: "score", value: pct },
        { name: "rest", value: 1 - pct },
    ];
    return (
        <div className="relative w-44 h-44 mx-auto">
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={data}
                        dataKey="value"
                        startAngle={90}
                        endAngle={-270}
                        innerRadius="78%"
                        outerRadius="100%"
                        stroke="none"
                    >
                        <Cell fill="#2148C0" />
                        <Cell fill="#E5E9F7" />
                    </Pie>
                </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-extrabold text-gray-800">{score}</span>
                <span className="text-xs text-gray-400">out of {max}</span>
            </div>
        </div>
    );
};

const Predictions = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const studentId = params.get("studentId");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPredictions = async () => {
            try {
                const url = new URL(`${API_BASE_URL}/api/predictions`);
                if (studentId) url.searchParams.set("studentId", studentId);
                const res = await fetch(url, { headers: authHeaders() });
                const json = await res.json();
                if (!res.ok) throw new Error(json.message || "Failed to load predictions");
                setData(json.data);
            } catch (err) {
                toast.error(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchPredictions();
    }, [studentId]);

    const jambSubjectNames = new Set((data?.jambSubjects || []).map((s) => s.subject));
    const confidence = confidenceFor(data?.jambTotalAttempts || 0);

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50/60 to-gray-50">
            <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex items-center gap-4">
                <button onClick={() => navigate(-1)} className="text-white">
                    <FaArrowLeft size={18} />
                </button>
                <h1 className="text-xl font-bold text-white">Predicted Performance</h1>
            </nav>

            <div className="max-w-3xl mx-auto p-6 space-y-6">
                {loading && (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center text-gray-400 text-sm">
                        Crunching practice history...
                    </div>
                )}

                {!loading && data?.predictedJambScore != null && (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
                        <ScoreGauge score={data.predictedJambScore} />
                        <p className="text-sm font-medium text-gray-700 mt-4">Projected JAMB score (best 4 subjects)</p>
                        <div className={`inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full text-xs font-medium ${confidence.tone}`}>
                            <FaClipboardCheck size={10} />
                            {confidence.label} — based on {data.jambTotalAttempts} practice question{data.jambTotalAttempts === 1 ? "" : "s"}
                        </div>
                        <p className="text-xs text-gray-400 mt-4 max-w-sm mx-auto">
                            Estimated from the recent practice trend, weighted by how much each subject has actually been practiced —
                            not a guarantee, just a heads-up on where things are headed.
                        </p>
                    </div>
                )}

                {!loading && (!data?.subjects || data.subjects.length === 0) && (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center text-gray-500 text-sm">
                        Not enough practice history yet to project a trend. Answer a few questions per subject and check back.
                    </div>
                )}

                {!loading && data?.subjects?.length > 0 && (
                    <div>
                        <h2 className="text-sm font-semibold text-gray-500 mb-3 px-1">By subject</h2>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {data.subjects.map((s) => (
                                <div
                                    key={s.subject}
                                    className={`bg-white rounded-2xl shadow-sm border p-4 ${
                                        jambSubjectNames.has(s.subject) ? "border-[#2148C0]/30" : "border-gray-100"
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <p className="font-semibold text-gray-800 text-sm">{s.subject}</p>
                                        {jambSubjectNames.has(s.subject) && (
                                            <span className="flex items-center gap-1 text-[10px] font-medium text-[#2148C0] bg-blue-50 rounded-full px-2 py-0.5">
                                                <FaStar size={8} /> Counted
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex items-end justify-between mt-3">
                                        <div>
                                            <p className="text-2xl font-bold text-gray-800">{s.projected != null ? `${s.projected}%` : "—"}</p>
                                            <p className="text-xs text-gray-400">{s.attempts} attempt{s.attempts === 1 ? "" : "s"}</p>
                                        </div>
                                        <span className={`text-xs font-semibold rounded-full px-2 py-1 ${gradeColor(s.predictedWaecGrade)}`}>
                                            {s.predictedWaecGrade ?? "—"}
                                        </span>
                                    </div>

                                    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3">
                                        <div
                                            className="h-1.5 rounded-full bg-[#2148C0] transition-all"
                                            style={{ width: `${s.projected ?? 0}%` }}
                                        />
                                    </div>

                                    <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-400">
                                        <TrendIcon trend={s.trend} /> {trendLabel(s.trend)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Predictions;
